import React from "react";
import { Box, Heading, Link, List, ListItem, Text, VStack } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";

function scrollToBlogSection(e) {
  e.preventDefault();
  const el = document.getElementById("blog-section");
  if (el) {
    el.scrollIntoView({ behavior: "smooth" });
    window.history.replaceState(null, '', '#blog-section');
  } else {
    window.location.href = "/blog#blog-section";
  }
}

export default function HowToBorrowSmartly() {
  return (
    <Box maxW="1000px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 20, md: 32 }}>
      <VStack spacing={6} align="start">
        <Link
          as="button"
          onClick={scrollToBlogSection}
          aria-label="Back to Blog"
          position="fixed"
          top={{ base: "84px", md: "96px" }}
          left={{ base: 4, md: 8 }}
          zIndex="sticky"
          bg="white"
          color="teal.600"
          fontWeight="semibold"
          border="1px solid"
          borderColor="teal.100"
          borderRadius="full"
          px={{ base: 0, sm: 4 }}
          py={{ base: 0, sm: 2 }}
          w={{ base: "42px", sm: "auto" }}
          h={{ base: "42px", sm: "auto" }}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="sm"
          _hover={{ textDecoration: "none", bg: "teal.50" }}
        >
          <Text as="span" display={{ base: "inline", sm: "none" }}>←</Text>
          <Text as="span" display={{ base: "none", sm: "inline" }}>← Back to Blog</Text>
        </Link>

        <Heading as="h1" size="xl" mb={2} mt={16}>How to Borrow Smartly Through the Year</Heading>
        <Text fontSize="sm" color="gray.500">
          By Heavenly • March 09, 2026 • 5 mins read
        </Text>

        <Text>
          Borrowing needs often follow seasonal patterns. Planning ahead by quarter helps reduce panic borrowing,
          improve approval outcomes, and keep repayment manageable.
        </Text>

        <Heading as="h2" size="md">Q1 (Jan-Mar): Reset and Stabilize</Heading>
        <Text>
          Start the year by reviewing balances, due dates, and your current debt-to-income profile.
        </Text>
        <List spacing={2} pl={4} styleType="disc">
          <ListItem>List all debts and minimum payments</ListItem>
          <ListItem>Set an emergency buffer target</ListItem>
          <ListItem>Avoid new borrowing until your budget is refreshed</ListItem>
        </List>

        <Heading as="h2" size="md">Q2 (Apr-Jun): Plan for Mid-Year Expenses</Heading>
        <Text>
          Repairs, school costs, and travel planning often begin in this period. Estimate needs before they become
          urgent.
        </Text>
        <List spacing={2} pl={4} styleType="disc">
          <ListItem>Compare loan options early instead of same-day borrowing</ListItem>
          <ListItem>Choose terms that match cash-flow, not just approval speed</ListItem>
          <ListItem>Keep utilization low before applying</ListItem>
        </List>

        <Heading as="h2" size="md">Q3 (Jul-Sep): Control Lifestyle Creep</Heading>
        <Text>
          Summer spending can increase quickly. This is where repayment discipline matters most.
        </Text>
        <List spacing={2} pl={4} styleType="disc">
          <ListItem>Track variable spending weekly</ListItem>
          <ListItem>Use smaller, targeted borrowing only when required</ListItem>
          <ListItem>Avoid stacking multiple short-term debts</ListItem>
        </List>

        <Heading as="h2" size="md">Q4 (Oct-Dec): Protect Year-End Cash Flow</Heading>
        <Text>
          Holiday and year-end obligations can pressure budgets. Enter this quarter with a clear repayment plan.
        </Text>
        <List spacing={2} pl={4} styleType="disc">
          <ListItem>Set a spending cap before promotions begin</ListItem>
          <ListItem>Prioritize paying down highest-cost debt first</ListItem>
          <ListItem>Avoid borrowing for non-essential purchases</ListItem>
        </List>

        <Heading as="h2" size="md">Seasonal Borrowing Rules That Work</Heading>
        <List spacing={2} pl={4} styleType="disc">
          <ListItem>Borrow for needs, not impulse spending</ListItem>
          <ListItem>Check total repayment, not only payment size</ListItem>
          <ListItem>Leave margin in your account to avoid NSF fees</ListItem>
          <ListItem>Recalculate affordability before taking a new loan</ListItem>
        </List>

        <Heading as="h2" size="md">Final Thoughts</Heading>
        <Text>
          Smart borrowing is seasonal planning plus repayment consistency. If you plan a quarter ahead, you reduce
          costly surprises and keep your credit profile stronger year-round.
        </Text>
        <Box mt={10} width="100%" textAlign="center">
          <Heading as="h3" size="md" mb={3} color="teal.700">Get an instant loan now with Swift Credit</Heading>
          <Link as={RouterLink} to="/apply" onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <Box as="button" bg="teal.500" color="white" px={8} py={3} borderRadius="md" fontWeight="bold" _hover={{ bg: "teal.600" }}>
              Get a Loan
            </Box>
          </Link>
        </Box>
      </VStack>
    </Box>
  );
}
